import React from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Input, Radio } from '../../atoms';
import { TooltipIcon } from '../../components';
import { StyledForm } from '../StyledForm';
import { Question } from '../Question';
import { Footer } from '../Footer';
import { FormFieldsContainer } from '../FormFieldsContainer';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { onboardingSubmitted } from '../events';
import { useSessionCustomer } from '../../customer';
import { schema } from './schema';

export { schema } from './schema';

export const CaloriesForm = ({
  defaultValues,
  weightLabel,
  weightQuestionPrompt,
  idealWeightLabel,
  puppyIdealWeightLabel,
  isPuppy,
  goBack,
  spayedChipLabel,
  spayedStatusQuestionCaption,
  spayedStatusQuestionPrompt,
  unspayedChipLabel,
  onSubmit,
  index,
  puppyIdealWeightHelp,
  puppySpayNeuterNotice,
  idealWeightHelp,
}) => {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful },
    reset,
    getValues,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  const reporter = useReporter();
  const { customer } = useSessionCustomer();

  const idealWeightCopy = isPuppy ? puppyIdealWeightLabel : idealWeightLabel;

  const idealWeightTooltip = (
    <TooltipIcon
      name="Display weight information"
      variant="info"
      placement="top"
    >
      {idealWeightHelp}
    </TooltipIcon>
  );

  const submitForm = async data => {
    reporter.tag(onboardingSubmitted(index, 'dog_weight', customer));
    const result = await onSubmit(data);
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };

  return (
    <StyledForm onSubmit={handleSubmit(submitForm)}>
      <FormFieldsContainer>
        <Question prompt={weightQuestionPrompt}>
          <Controller
            control={control}
            name="weight"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Input
                {...field}
                type="number"
                labelText={weightLabel}
                value={field.value || ''}
                status={error?.message ? 'error' : null}
                error={error?.message || ''}
                visuallyHideLabel
              />
            )}
          />

          <Controller
            control={control}
            name="idealWeight"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Input
                {...field}
                type="number"
                labelText={idealWeightCopy}
                icon={idealWeightTooltip}
                value={field.value || ''}
                status={error?.message ? 'error' : null}
                error={error?.message || ''}
                visuallyHideLabel
              />
            )}
          />
        </Question>

        <Question
          prompt={spayedStatusQuestionPrompt}
          caption={spayedStatusQuestionCaption}
        >
          <Controller
            control={control}
            name="spayedStatus"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <>
                <Radio
                  {...field}
                  legendText="spayed status"
                  value={`${field.value}`}
                  choices={[
                    { name: spayedChipLabel, value: 'true' },
                    { name: unspayedChipLabel, value: 'false' },
                  ]}
                  error={error?.message || ''}
                  status={error?.message ? 'error' : null}
                  onChange={e => {
                    field.onChange(e);
                    trigger(field.name);
                  }}
                />
              </>
            )}
          />
        </Question>
      </FormFieldsContainer>
      <Footer
        isBackButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        isForwardButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        onBackButtonClick={goBack}
        isForwardButtonLoading={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
      />
    </StyledForm>
  );
};

CaloriesForm.displayName = 'CaloriesForm';

CaloriesForm.propTypes = {
  defaultValues: PropTypes.shape({
    weight: PropTypes.number,
    idealWeight: PropTypes.number,
    spayedStatus: PropTypes.bool,
  }).isRequired,
  weightLabel: PropTypes.string.isRequired,
  weightQuestionPrompt: PropTypes.string.isRequired,
  idealWeightLabel: PropTypes.string.isRequired,
  isPuppy: PropTypes.bool.isRequired,
  goBack: PropTypes.func.isRequired,
  spayedChipLabel: PropTypes.string.isRequired,
  spayedStatusQuestionCaption: PropTypes.string.isRequired,
  spayedStatusQuestionPrompt: PropTypes.string.isRequired,
  unspayedChipLabel: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  puppyIdealWeightHelp: PropTypes.string.isRequired,
  puppySpayNeuterNotice: PropTypes.string.isRequired,
  idealWeightHelp: PropTypes.string.isRequired,
  puppyIdealWeightLabel: PropTypes.string.isRequired,
};
